import { useCallback, useEffect, useReducer, useState } from 'react';

import { Checkbox, FormControlLabel, InputLabel } from '@material-ui/core';
import { openDatePickerAction } from 'actions/datePicker';
import ConfirmationModal from 'components/ConfirmationModal';
import { format } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { components } from 'react-select';
import AsyncSelect from 'react-select/async';
import CreatableSelect from 'react-select/creatable';
import { findGradesAndRolesOfFunction } from 'screens/admin/FunctionAndGrade/Helpers';
import { selectLoggedInEmployeeData, selectLoggedInEmployeeId } from 'selectors/loggedInEmployee';
import styled, { css } from 'styled-components';
import { HeadingText, MeshAsyncCreatableSelect, MeshCreatableSelect, MeshSelect, StyledDrawer } from 'styles/admin';
import {
  Divider1,
  HideScrollbarCSS,
  NewStyledIconButton,
  StyledBox,
  StyledTextField,
  StyledTooltip,
  StyledTypography,
  VerticallyAlignedFlexContainer,
} from 'styles/common';
import { checkCustomRenameLocationToSubsidiaryEnabled } from 'utils/helpers/featureFlags';

import { EMPLOYEE_NAME_REGEX, TEAM_TYPE, USER_ROLES } from 'constants/ProjectConstants';
import { EMPLOYEE_FIELDS_LIST } from 'constants/employeeFieldsList';
import { StyledCloseIcon } from 'constants/icons';
import theme from 'constants/theme';

import { addNewEmployee, getOrganizationFunctions, searchEmployeeByEmail, updateEmployee } from 'services/admin';
import { getEmployeeTeams } from 'services/employee';
import { departmentAutoComplete } from 'services/search';
import { showErrorSnackbar, showSuccessSnackbar } from 'services/snackbar';
import { removeEmployee } from 'services/team';

import { Typography4 } from 'uiToolkit/Typography';

import { FunctionSelection } from '../../Competencies/CompetencieByFunctionAndRole/CompetencyByFunctionAndRole';
import { AdminRolesMultiValueContainer, AdminRolesOptions } from '../../MyProfile/commonStyles';
import { DateField, TextField, TextFieldWithValidation } from '../helperComponents';
import { StyledControl, StyledDivider, StyledForm, StyledMeshSelect, StyledPanelHeader } from '../styles';
import EmployeeSidePanelFooter from './EmployeeSidePanelFooter';

// admin access components

const SelectAllClearAll = ({ options, id, handleMultiSelectChange }) => {
  const selectAll = () => {
    handleMultiSelectChange(id, options);
  };
  const selectNone = () => {
    handleMultiSelectChange(id, []);
  };
  return (
    <>
      <Divider1 />
      <VerticallyAlignedFlexContainer
        style={{ padding: '1rem 1.5rem', cursor: 'default', justifyContent: 'space-between' }}
      >
        <Typography4 customColor="primary" onClick={selectAll}>
          Select All
        </Typography4>
        <Typography4 customColor="primary" leftGutter={3} onClick={selectNone}>
          Select None
        </Typography4>
      </VerticallyAlignedFlexContainer>
    </>
  );
};

const AdminAccessMenu = (props) => (
  <>
    <components.MenuList {...props}>{props.children}</components.MenuList>
    <SelectAllClearAll options={props.options} id={props.id} handleMultiSelectChange={props.handleMultiSelectChange} />
  </>
);

/* This is to fix zIndex issue for react select dropdowns */
const customStyles = {
  menu: (provided, state) => ({
    ...provided,
    zIndex: 10,
  }),
  container: (provided) => ({
    ...provided,
    marginTop: '9px',
  }),
};
const adminRoleStyles = {
  menu: (provided, state) => ({
    ...provided,
    zIndex: 10,
  }),
  container: (provided) => ({
    ...provided,
    marginTop: '9px',
  }),
};

const initState = {
  employee: EMPLOYEE_FIELDS_LIST.reduce(
    (stateObj, { id, defaultValue }) => ({
      ...stateObj,
      [id]: defaultValue,
    }),
    {}
  ),
};

const reducer = (state, action) => {
  const { type, payload } = action;
  switch (type) {
    case 'UPDATE_VALUES':
      return { ...state, employee: { ...state.employee, ...payload } };
    case 'RESET_STATE': {
      return { ...initState };
    }
    default:
      return state;
  }
};

const AddEmployeePanel = ({ open, onCancel, onSuccess, data, mode, reloadData }) => {
  const dispatch = useDispatch();
  const [{ employee }, localDispatch] = useReducer(reducer, initState);
  const [loading, setLoading] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);

  const [allPodOptions, setPodOptions] = useState([]);
  const [employeePods, setEmployeePods] = useState([]);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [clearedPod, setClearedPod] = useState({
    name: null, // this is field name
    option: null,
  });

  const loggedInEmployeeId = useSelector(selectLoggedInEmployeeId);
  const LoggedInEmployeeObj = useSelector(selectLoggedInEmployeeData);

  const convertStateToTempState = (currentState) => {
    let tempState = {};
    currentState.forEach((v) => {
      tempState = {
        ...tempState,
        [v.uuid]: {
          ...v,
          jobGradeList: Array.isArray(v.jobGradeList)
            ? v.jobGradeList.reduce((a, b) => ({ ...a, [b.uuid]: { ...b } }), {})
            : {},
        },
      };
    });
    return tempState;
  };

  // functions and levels
  const [functions, setFunctions] = useState(null);
  const [loadingFunctions, setLoadingFunctions] = useState(true);
  const fetchData = () => {
    getOrganizationFunctions()
      .then((res) => setFunctions(res))
      .catch((err) => {
        console.error('Error in fetching functions : ', err);
        dispatch(showErrorSnackbar(err.message));
      })
      .finally(() => {
        setLoadingFunctions(false);
      });
  };
  useEffect(() => {
    fetchData();
  }, []);
  // end of functions and levels

  const handleInputChange = (controlId, value) => {
    localDispatch({
      type: 'UPDATE_VALUES',
      payload: { [controlId]: value },
    });
  };

  const handleMultiSelectChange = (controlId, values) => {
    const valuesArray = values.map((v) => v.value);
    localDispatch({
      type: 'UPDATE_VALUES',
      payload: { [controlId]: valuesArray },
    });
  };
  const openDatePicker = (controlId) => {
    dispatch(
      openDatePickerAction({
        clearable: false,
        autoOk: false,
        onChange: (date) => handleDateChange(controlId, date),
        initialDate: employee[controlId],
      })
    );
  };

  const handleDateChange = (controlId, date) => {
    if (date) {
      const dateValue = format(new Date(date), 'yyyy-MM-dd');
      handleInputChange(controlId, dateValue);
    }
  };

  const checkAndUpdateEmployeeData = () => {
    if (mode === 'edit') {
      const primaryAndSecondaryPodOptions = getPrimaryAndSecondaryPodOptions(data);

      let originalPods = [];

      if (primaryAndSecondaryPodOptions.primaryPod) originalPods = [primaryAndSecondaryPodOptions.primaryPod];

      if (primaryAndSecondaryPodOptions.secondaryPods)
        originalPods = [...originalPods, ...primaryAndSecondaryPodOptions.secondaryPods];

      if (isEmployeeRemovedFromPod(originalPods, employeePods)) reloadData();
    }
  };

  const handleKeyDown = ({ keyCode }) => {
    if (keyCode === 27) {
      checkAndUpdateEmployeeData();
      onCancel();
    }
  };

  const fetchAllPods = () => {
    getEmployeeTeams({ employeeId: loggedInEmployeeId })
      .then(({ orgTeams, employeeTeams }) => {
        const filteredOwnPods = employeeTeams
          .filter((team) => team.teamType === TEAM_TYPE.other || team.teamType === TEAM_TYPE.preferred)
          .filter((team) => orgTeams.every((pod) => pod.id !== team.id))
          .map(({ id, name }) => ({
            value: id,
            label: name,
          }));

        const mappedOrgPods = orgTeams.map(({ id, name }) => ({
          value: id,
          label: name,
        }));

        setPodOptions(
          [...filteredOwnPods, ...mappedOrgPods].sort((pod1, pod2) => pod1.label.localeCompare(pod2.label))
        );
      })
      .catch((err) => {
        dispatch(showErrorSnackbar(err.message || 'Pod option fetching failed. Please close and reopen sidePanel'));
        setPodOptions([]);
      });
  };

  const fixSecondarySelectedPods = (selectedPod, removedPod) => {
    let filteredSecondaryPods = [];

    if (employee.secondaryPods && employee.secondaryPods.length > 0)
      filteredSecondaryPods = employee.secondaryPods.filter((pod) => pod.value !== selectedPod.value);

    if (removedPod !== null) {
      handleInputChange(
        'secondaryPods',
        filteredSecondaryPods.length > 0 ? [...filteredSecondaryPods, removedPod] : [removedPod]
      );
    } else {
      handleInputChange('secondaryPods', filteredSecondaryPods.length > 0 ? filteredSecondaryPods : null);
    }
  };

  const handleNewOptionCreation = (label, isPrimaryPod) => {
    if (!label || label === '') return;

    const option = { value: label, label };
    setPodOptions((prev) => [...prev, option].sort((pod1, pod2) => pod1.label.localeCompare(pod2.label)));

    if (isPrimaryPod) handleInputChange('primaryPod', option);
    else handleInputChange('secondaryPods', [...employee.secondaryPods, option]);
  };

  const afterPodRemoval = () => {
    // remove clearedPod from employee pods
    // this will ensure that no confirmation popup will come for cleared pod, in case it is added back
    setEmployeePods((prev) => prev.filter(({ value }) => clearedPod.option.value !== value));

    if (clearedPod.name === 'primaryPod') {
      handleInputChange('primaryPod', null);
    } else {
      const filteredSecondaryPods = employee.secondaryPods.filter(({ value }) => value !== clearedPod.option.value);
      handleInputChange('secondaryPods', filteredSecondaryPods);
    }
  };

  const onPodRemovalConfirmation = () => {
    removeEmployeeFromPod(clearedPod.option.value);
  };

  const handlePodRemoval = (removedPod, name) => {
    setClearedPod({
      name,
      option: removedPod,
    });
    setIsConfirmationModalOpen(true);
  };

  const removeEmployeeFromPod = (podId) => {
    setLoading(true);
    removeEmployee({ employeeId: data.id, teamId: podId })
      .then((res) => {
        dispatch(showSuccessSnackbar('Employee is successfully removed from the pod'));
        afterPodRemoval();
      })
      .catch((err) => {
        dispatch(showErrorSnackbar(err.message || 'Employee removal from the pod failed.'));
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const fetchDepartmentOptions = (text) =>
    departmentAutoComplete({
      tagsSet: [{ tagType: 'DEPARTMENT', limit: 10 }],
      text,
    })
      .then((result) =>
        result.map(({ displayName }) => ({
          value: displayName,
          label: displayName,
        }))
      )
      .catch((err) => {
        dispatch(showErrorSnackbar(err.message));
      });

  const AdminMenuListWrapper = useCallback(
    (props) => (
      <AdminAccessMenu
        handleMultiSelectChange={props.selectProps.handleMultiSelectChange}
        id={props.selectProps.id}
        {...props}
      />
    ),
    []
  );
  const getControlComponent = (control) => {
    const { id, title, type, options } = control;
    if (
      (id === 'department' || id === 'ethnicity') &&
      employee[id] &&
      options?.findIndex((opt) => opt.value === employee[id]) === -1
    ) {
      options.push({ value: employee[id], label: employee[id] });
    }
    switch (type) {
      case 'date':
        // https://stackoverflow.com/a/52352512/8487836
        const date = new Date(employee[id]);
        const dateOnly = new Date(date.valueOf() + date.getTimezoneOffset() * 60 * 1000);
        return (
          <DateField
            control={control}
            value={employee[id] ? dateOnly : null}
            openDatePicker={openDatePicker}
            loading={loading}
          />
        );
      case 'select':
        return (
          <StyledMeshSelect
            styles={customStyles}
            classNamePrefix="select"
            placeholder={title}
            onChange={({ value }) => handleInputChange(id, value)}
            name={id}
            value={options.filter((opt) => opt.value === employee[id])[0]}
            options={options}
            isDisabled={loading}
            zIndex="8"
          />
        );
      case 'customMultiSelect':
        return (
          <StyledMeshMultiSelect
            classNamePrefix="custom-multi-select"
            styles={adminRoleStyles}
            placeholder="Employee Access"
            onChange={(values) => handleMultiSelectChange(id, values)}
            name={id}
            value={USER_ROLES({
              enabledFeatures: LoggedInEmployeeObj.enabledFeatures,
            }).filter((opt) => employee[id]?.includes(opt.value))}
            options={USER_ROLES({
              enabledFeatures: LoggedInEmployeeObj.enabledFeatures,
            })}
            isDisabled={loading}
            zIndex="auto"
            isMulti
            hideSelectedOptions={false}
            components={{
              Option: AdminRolesOptions,
              MenuList: AdminMenuListWrapper,
              MultiValueContainer: AdminRolesMultiValueContainer,
            }}
            isClearable={false}
            isSearchable={false}
            closeMenuOnSelect={false}
            $adminAccess
            id={id}
            handleMultiSelectChange={handleMultiSelectChange}
          />
        );
      case 'creatableSelect':
        return (
          <MeshCreatableSelect
            classNamePrefix="select"
            styles={customStyles}
            placeholder={title}
            onChange={({ value }) => handleInputChange(id, value)}
            name={id}
            value={options.filter((opt) => opt.value === employee[id])[0]}
            options={options}
            isDisabled={loading}
            width="100%"
          />
        );
      // case 'asyncCreatableSelect':
      //   return (
      //     <MeshAsyncCreatableSelect
      //       classNamePrefix="select"
      //       styles={customStyles}
      //       placeholder={title}
      //       onChange={({ value }) => handleInputChange(id, value)}
      //       name={id}
      //       value={options.filter((opt) => opt.value === employee[id])[0]}
      //       loadOptions={fetchDepartmentOptions}
      //       isDisabled={loading}
      //       width="100%"
      //       defaultOptions
      //     />
      //   );

      case 'email-autocomplete':
        return (
          <AsyncSelect
            styles={customStyles}
            defaultOptions
            classNamePrefix="select"
            placeholder={title}
            value={employee[id] !== '' ? { value: employee[id], label: employee[id] } : undefined}
            onChange={(option) => {
              // clearable makes option as null, so need to handle that case separately.
              if (option === null) handleInputChange(id, '');
              else handleInputChange(id, option.value);
            }}
            name={id}
            loadOptions={fetchEmailOptions}
            isDisabled={loading}
            isClearable
          />
        );
      // case 'primaryPod-select': {
      //   return (
      //     <CreatableSelect
      //       menuPlacement="auto"
      //       isClearable
      //       styles={customStyles}
      //       classNamePrefix="select"
      //       placeholder={title}
      //       value={employee[id] && employee[id] !== '' ? employee[id] : null}
      //       onChange={(option, { action, name }) => {
      //         // clearable makes option as null, so need to handle that case separately.
      //         if (mode === 'edit') {
      //           if (action === 'clear') {
      //             console.log('primary-pod', action);
      //             handlePodRemoval(employee.primaryPod, name);
      //             return;
      //           }
      //         }
      //         fixSecondarySelectedPods(option, employee.primaryPod);
      //         handleInputChange(id, option);
      //       }}
      //       onCreateOption={(option) => handleNewOptionCreation(option, true)}
      //       name={id}
      //       options={allPodOptions}
      //       isDisabled={loading}
      //     />
      //   );
      // }
      // case 'secondaryPods-select': {
      //   return (
      //     <StyledCreatableSelect
      //       isMulti
      //       isClearable={false}
      //       backspaceRemovesValue={false}
      //       styles={adminRoleStyles}
      //       // className="basic-multi-select"
      //       classNamePrefix="custom-multi-select"
      //       menuPlacement="auto"
      //       placeholder={title}
      //       onChange={(option, { action, removedValue, name }) => {
      //         // clearable makes option as null, so need to handle that case separately.

      //         if (mode === 'edit') {
      //           if (action === 'remove-value') {
      //             const isEmployeePartOfRemovedPod = employeePods.some(({ value }) => value === removedValue.value);

      //             if (isEmployeePartOfRemovedPod) {
      //               handlePodRemoval(removedValue, name);
      //               return;
      //             }
      //           }
      //         }

      //         handleInputChange(id, option);
      //       }}
      //       defaultValue={employee[id]}
      //       value={employee[id]}
      //       name={id}
      //       options={allPodOptions}
      //       onCreateOption={(option) => handleNewOptionCreation(option, false)}
      //       isDisabled={loading}
      //       isOptionDisabled={({ value }) => employee.primaryPod && employee.primaryPod.value === value}
      //       hideSelectedOptions={false}
      //       components={{
      //         Option: AdminRolesOptions,
      //         MultiValueContainer: AdminRolesMultiValueContainer,
      //       }}
      //     />
      //   );
      // }
      case 'text':
        if (id === 'firstName' || id === 'lastName' || id === 'preferredName') {
          return (
            <TextFieldWithValidation
              validationRegex={EMPLOYEE_NAME_REGEX}
              helperText={'<, >, ", \' are not allowed'}
              control={control}
              value={employee[id]}
              onBlur={handleInputChange}
              loading={loading}
            />
          );
        }

        return <TextField control={control} value={employee[id]} onBlur={handleInputChange} loading={loading} />;
      default:
        return <TextField control={control} value={employee[id]} onBlur={handleInputChange} loading={loading} />;
    }
  };

  const submitFormHelper = () => {
    if (mode === 'add') return addNewEmployee(employee, sendEmail);

    const employeeObj = { ...data, ...employee };
    return updateEmployee(employeeObj, data.id);
  };

  const submitFormData = () => {
    if (!employee.firstName || employee.firstName === '') {
      dispatch(showErrorSnackbar('First name is missing.'));
      return;
    }

    if (!employee.email || employee.email === '') {
      dispatch(showErrorSnackbar('Email is missing.'));
      return;
    }

    // if (!employee.userRole || employee.userRole === '') {
    //   dispatch(showErrorSnackbar('Access info is missing.'));
    //   return;
    // }

    if (!isNameValid(employee.firstName)) {
      dispatch(showErrorSnackbar("Unsupported characters in Employee's first name."));
      return;
    }

    if (!isNameValid(employee.lastName)) {
      dispatch(showErrorSnackbar("Unsupported characters in Employee's last name."));
      return;
    }

    setLoading(true);

    submitFormHelper()
      .then((responseEmp) => {
        dispatch(showSuccessSnackbar(`Employee successfully ${mode === 'add' ? 'added' : 'updated'}`));
        onSuccess(responseEmp);
      })
      .catch((err) => {
        dispatch(showErrorSnackbar(err?.message ?? 'Unable to add employee. Please try again'));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const validateData = (employee) => {
    let isValid = true;

    isValid = isValid && employee.firstName && employee.firstName !== '';
    isValid = isValid && employee.email && employee.email !== '';
    isValid = isValid && employee.userRole && employee.userRole !== '';

    return isValid;
  };

  const isNameValid = (name) => EMPLOYEE_NAME_REGEX.test(name);

  const fetchEmailOptions = (text) =>
    searchEmployeeByEmail({
      tagType: 'EMPLOYEE_EMAIL',
      query: text,
      widgetType: 'TASK',
    })
      .then((result) =>
        result.map(({ email }) => ({
          value: email,
          label: email,
          isDisabled: employee.email.toLowerCase() === email.toLowerCase(),
        }))
      )
      .catch((err) => {
        dispatch(showErrorSnackbar(err.message));
      });

  useEffect(() => {
    // on mode == edit , populate the state with existing employee values.
    if (mode === 'edit') {
      const employeeObj = Object.keys(employee).reduce((obj, key) => {
        if (key === 'preferredName' && data.metadata && data.metadata.preferredName) {
          obj[key] = data.metadata.preferredName;
        } else if (data.hasOwnProperty(key)) {
          obj[key] = data[key];
        }
        return obj;
      }, {});
      const primaryAndSecondaryPodOptions = getPrimaryAndSecondaryPodOptions(data);

      let employeePods = [];

      if (primaryAndSecondaryPodOptions.primaryPod) employeePods = [primaryAndSecondaryPodOptions.primaryPod];

      if (primaryAndSecondaryPodOptions.secondaryPods)
        employeePods = [...employeePods, ...primaryAndSecondaryPodOptions.secondaryPods];

      setEmployeePods(employeePods);
      localDispatch({
        type: 'UPDATE_VALUES',
        payload: {
          ...employeeObj,
          jobFunctionId: data.jobFunctionId,
          jobGradeUI: data.jobGradeUI,
          jobRoleId: data.jobRoleId,
          externalEmployeeId: data.externalEmployeeId,
          ...primaryAndSecondaryPodOptions,
        },
      });
    }
  }, [data, mode]);

  useEffect(() => {
    // on close of side Panel, clear the state.
    if (!open) {
      setEmployeePods([]);
      localDispatch({
        type: 'RESET_STATE',
      });
    } else {
      // on every open and closing of sidePanel make a call fetch updated list of Pods.
      fetchAllPods();
    }
  }, [open]);

  const Header = () => (
    <StyledPanelHeader>
      <HeadingText>{mode === 'edit' ? 'Edit Employee' : 'Add Employee'}</HeadingText>

      <NewStyledIconButton
        onClick={() => {
          checkAndUpdateEmployeeData();
          onCancel();
        }}
      >
        <StyledCloseIcon color={theme.palette.icons.grey2} size="tiny" />
      </NewStyledIconButton>
    </StyledPanelHeader>
  );

  const SendInviteEmailCheckbox = (
    <div style={{ width: '100%' }}>
      <FormControlLabel
        control={
          <Checkbox
            checked={sendEmail}
            onChange={({ target }) => {
              setSendEmail(target.checked);
            }}
            name="sendEmail"
            color="primary"
          />
        }
        label="Send invite email"
      />
    </div>
  );

  const rolesAndGradesForCurrentFunction = findGradesAndRolesOfFunction(functions ?? [], employee.jobFunctionId);

  const gradesForCurrentFunction =
    rolesAndGradesForCurrentFunction?.grades?.map((grade) => ({
      value: grade.grade.uuid,
      label: grade.grade.name,
    })) ?? [];

  const rolesForCurrentRole =
    rolesAndGradesForCurrentFunction?.grades
      ?.find((grade) => parseInt(grade.grade.uuid) === employee.jobGradeUI)
      ?.roles?.map((role) => ({ value: role.role.uuid, label: role.role.name })) ?? [];

  const locationTitle = checkCustomRenameLocationToSubsidiaryEnabled() ? 'Subsidiary' : 'Location';

  return (
    <StyledDrawer
      anchor="right"
      open={open}
      ModalProps={{
        onBackdropClick: () => {
          checkAndUpdateEmployeeData();
          onCancel();
        },
      }}
      onKeyDown={handleKeyDown}
    >
      <StyledBox height="100%" display="flex" flexDirection="column" overflow="auto">
        <Header />
        <StyledDivider />
        <StyledContainer>
          <OverflowContainer onSubmit={(ev) => ev.preventDefault()}>
            {EMPLOYEE_FIELDS_LIST.map((c) => {
              const control = c.id === 'location' ? { ...c, title: locationTitle } : c;
              return (
                <StyledControl key={control.id}>
                  <InputLabel htmlFor={control.id}>
                    <StyledTypography component="span" cursor="pointer" color="#000" variant="body2">
                      {control.title}
                      {control.isRequired ? ' *' : ''}
                    </StyledTypography>
                  </InputLabel>
                  {getControlComponent(control)}
                </StyledControl>
              );
            })}
            <FieldContainer>
              <InputLabel>
                <StyledTypography component="span" cursor="pointer" color="#000" variant="body2">
                  Function
                </StyledTypography>
              </InputLabel>
              <FunctionSelection
                setSelectedFunction={(value) => {
                  handleInputChange('jobFunctionId', parseInt(value));
                  handleInputChange('jobGradeUI', null);
                  handleInputChange('jobRoleId', null);
                }}
                selectedFunction={String(employee.jobFunctionId)}
                isDropdownOnly
              />
            </FieldContainer>
            <StyledTooltip
              title={loading || !employee.jobFunctionId ? 'Please select a Function before selecting a Grade' : ''}
            >
              <FieldContainer>
                <InputLabel>
                  <StyledTypography component="span" cursor="pointer" color="#000" variant="body2">
                    Grade
                  </StyledTypography>
                </InputLabel>
                <StyledMeshSelect
                  styles={customStyles}
                  classNamePrefix="select"
                  placeholder="Grade"
                  onChange={({ value }) => {
                    handleInputChange('jobGradeUI', parseInt(value));
                    handleInputChange('jobRoleId', null);
                  }}
                  name="jobGradeUI"
                  value={gradesForCurrentFunction.filter((opt) => opt.value === String(employee.jobGradeUI))[0] ?? ''}
                  options={gradesForCurrentFunction}
                  isDisabled={loading || !employee.jobFunctionId}
                  zIndex="8"
                />
              </FieldContainer>
            </StyledTooltip>
            <StyledTooltip
              title={loading || !employee.jobGradeUI ? 'Please select a Grade before selecting a Role' : ''}
            >
              <FieldContainer>
                <InputLabel>
                  <StyledTypography component="span" cursor="pointer" color="#000" variant="body2">
                    Role
                  </StyledTypography>
                </InputLabel>
                <StyledMeshSelect
                  styles={customStyles}
                  classNamePrefix="select"
                  placeholder="Role"
                  onChange={({ value }) => handleInputChange('jobRoleId', parseInt(value))}
                  name="jobRoleId"
                  value={rolesForCurrentRole.filter((opt) => opt.value === String(employee.jobRoleId))[0] ?? ''}
                  options={rolesForCurrentRole}
                  isDisabled={loading || !employee.jobGradeUI}
                  zIndex="8"
                />
              </FieldContainer>
            </StyledTooltip>

            <FieldContainer>
              <InputLabel>
                <StyledTypography component="span" cursor="pointer" color="#000" variant="body2">
                  External Employee Id
                </StyledTypography>
              </InputLabel>
              <TextField
                control={{ id: 'externalEmployeeId', title: 'External Employee Id' }}
                value={employee?.externalEmployeeId}
                onBlur={handleInputChange}
                loading={loading}
              />
            </FieldContainer>

            {mode === 'add' && SendInviteEmailCheckbox}
          </OverflowContainer>
        </StyledContainer>
      </StyledBox>
      <Divider1 />
      <EmployeeSidePanelFooter loading={loading} mode={mode} onCancel={onCancel} onSubmit={submitFormData} />
      <ConfirmationModal
        open={isConfirmationModalOpen}
        title="Are you sure you want to continue?"
        description={`This will remove this employee from ${
          clearedPod.option && clearedPod.option.label
        } pod. This action cannot be undone.`}
        onConfirm={() => {
          onPodRemovalConfirmation();
          setIsConfirmationModalOpen(false);
        }}
        onCancel={() => {
          setIsConfirmationModalOpen(false);
        }}
      />
    </StyledDrawer>
  );
};

const getPrimaryAndSecondaryPodOptions = (employee) => {
  let primaryPod = null;
  let secondaryPods = null;

  if (employee.teams && employee.teams.length > 0) {
    const employeePods = employee.teams.filter((team) => team.teamType !== TEAM_TYPE.hris);
    const filteredPrimaryPods = employeePods.filter((pod) => pod.isPrimaryPod());
    const filteredSecondaryPods = employeePods.filter((pod) => pod.teamType === TEAM_TYPE.other);

    primaryPod =
      filteredPrimaryPods.length > 0
        ? {
            value: filteredPrimaryPods[0].id,
            label: filteredPrimaryPods[0].name,
          }
        : null;

    secondaryPods =
      filteredSecondaryPods.length > 0
        ? filteredSecondaryPods.map((pod) => ({
            value: pod.id,
            label: pod.name,
          }))
        : null;
  }

  return { primaryPod, secondaryPods };
};

const isEmployeeRemovedFromPod = (originalPods, currentPods) => {
  const removedEmployeePods = originalPods.filter((pod) => currentPods.every((cpod) => cpod.value !== pod.value));

  return removedEmployeePods.length > 0;
};

const FieldContainer = styled.div`
  width: 48%;
  display: inline-block;
  margin-bottom: 0.75rem;
  vertical-align: top;
  ${(props) =>
    props.disabled &&
    css`
      .MuiFormControl-root,
      .select__control {
        background: #f7f7f7;
        opacity: 0.8;
      }
      pointer-events: none;
    `}
`;
const JobFunctionTextField = styled(StyledTextField)`
  & .MuiInputBase-root {
    color: rgba(0, 0, 0, 0.87);
  }
  & input {
    cursor: pointer;
  }
`;

const StyledMeshMultiSelect = styled(MeshSelect)`
  .custom-multi-select__option {
    border-color: transparent;
    background-color: transparent;
  }
  .custom-multi-select__option:active {
    border-color: transparent;
    background-color: transparent;
  }
`;

const StyledCreatableSelect = styled(CreatableSelect)`
  .custom-multi-select__option {
    border-color: transparent;
    background-color: transparent;
  }
  .custom-multi-select__option:active {
    border-color: transparent;
    background-color: transparent;
  }
  // .custom-multi-select__value-container {
  //   display: -webkit-box;
  //   -webkit-line-clamp: 3;
  //   -webkit-box-orient: vertical;
  //   overflow: hidden;
  //   padding: 0.5rem;
  // }
`;

const OverflowContainer = styled(StyledForm)`
  ${HideScrollbarCSS};
  overflow-y: auto;
  position: relative;
  width: 100%;
`;

const StyledContainer = styled.div`
  display: flex;
  position: relative;
  flex: 1;
  overflow: hidden;
  margin: 0;
`;

export default AddEmployeePanel;
